import {
  SEARCH_FACETS,
  SEARCH_FILTER_TYPE,
  SUGGESTION_TYPES,
  PURCHASE_HISTORY_PAGE_SIZE,
} from '@/features/shared/constants/global'
import { BUY_AGAIN_SINGLE_RETAILER_TYPE } from '@/features/shared/constants/shelves'
import { normalizeCoupon } from '@/features/shop/utils/normalizeCoupon'
import { normalizeProduct } from '@/features/shared/utils/normalizeProduct'
import { type ProductAPI } from '@/features/shop/services/Product/types'
import { normalizeShelf } from '@/features/shop/services/Shelves/utils'
import { type Shelf } from '@/features/shop/services/Shelves/types'
import {
  type Endcap,
  type EndcapBySlotPos,
  type SearchProductAPI,
  type SearchResponse,
  type SearchResults,
  type Facet,
  type CategoryFacetItemAPI,
  type SearchCategory,
  type CategoryFacet,
  type FacetAPI,
  type SearchParams,
  type CouponFacetResponse,
} from '@/features/shop/services/Search/types'
import {
  type Suggestion,
  type SuggestionsResponse,
} from '@/features/shared/services/Autocomplete/types'
import { type ShoppingStoreDetails } from '@/features/shop/services/ShoppingStore/types'
import { without } from '@/features/shared/utils/without'
import { validSearchFilterParams } from '@/features/shop/services/Search/constants'
import { type ParsedUrlQuery } from 'node:querystring'
import { logError } from '@/features/shared/utils/logger'

const { ACTIVE_DEALS, CATEGORY, ON_SALE } = SEARCH_FILTER_TYPE

export const getBuyAgainShelf = <T>(shelves: Shelf<T>[] = []): Shelf<T> | undefined =>
  shelves.find((shelf) => shelf.type === BUY_AGAIN_SINGLE_RETAILER_TYPE && shelf.items.length > 0)

export const normalizeCategoryItem = (category: CategoryFacetItemAPI): SearchCategory => {
  const lineages = category.name.split(' > ')
  const lineageDepth = lineages.length - 1
  const name = lineages[lineageDepth] || category.name
  return {
    ...category,
    id: category.id,
    name,
    lineageDepth,
    lineage: category.name,
  }
}

// used for items returned for browse (with any category selected)
export const normalizeBrowseItems = (
  categoryItems: CategoryFacetItemAPI[] = [],
): SearchCategory[] =>
  categoryItems.map((category) => {
    const normalizedCategory = normalizeCategoryItem(category)
    const { lineageDepth } = normalizedCategory

    if (lineageDepth) {
      const parentCategory = categoryItems[lineageDepth - 1]
      if (parentCategory) {
        const { id, name } = normalizeCategoryItem(parentCategory)
        return { ...normalizedCategory, parentId: id, parentName: name }
      }
    }
    return normalizedCategory
  })

// used for items returned for search (without a category selected)
export const normalizeSearchItems = (
  categoryItems: CategoryFacetItemAPI[] = [],
): SearchCategory[] =>
  categoryItems.reduce<SearchCategory[]>((result, category) => {
    const normalizedCategory = normalizeCategoryItem(category)
    const lineages = category.name.split(' > ')
    // we only need to store the H2 categories for the very first
    // H1 category returend from the search API in order to auto expand
    // the filters of the H1 category
    if (lineages.length > 1) {
      const [firstH1Category] = categoryItems
      // filter out H2 category out if its parent is not the first H1 category
      if (lineages[0] === firstH1Category?.name) {
        const { name: parentName, id: parentId } = firstH1Category || {}
        return [...result, { ...normalizedCategory, parentName, parentId }]
      }
      return result
    }
    return [...result, normalizedCategory]
  }, [])

export const normalizeFacets = (facets: FacetAPI[], isBrowsing = false): Facet[] => {
  if (!Array.isArray(facets)) return []
  return facets
    .map((f) => {
      if (f.facet === SEARCH_FILTER_TYPE.CATEGORY) {
        const items = isBrowsing ? normalizeBrowseItems(f.items) : normalizeSearchItems(f.items)
        return { ...f, items }
      }
      return f
    })
    .reduce<Facet[]>((acc, f) => {
      // backend is currently sending back the on sale facet even though we don't request
      // for it since the mobile team needs it for backward compatibility
      if (f.facet === ON_SALE) return acc
      return f.facet === ACTIVE_DEALS ? [f, ...acc] : [...acc, f]
    }, [])
}

export const normalizeCouponFacets = (facets: CouponFacetResponse[] = []): Facet[] =>
  facets.flatMap((facet) => {
    if (facet.id !== SEARCH_FILTER_TYPE.CATEGORY) return []
    return [
      {
        name: facet.name,
        facet: facet.id,
        items: facet.options.map((option) => ({
          id: Number(option.id),
          name: option.name,
          lineageDepth: 0,
          lineage: option.name,
        })),
      },
    ]
  })

export const normalizeEndcaps = (endcaps: Endcap[] = []): EndcapBySlotPos => {
  const endcapsBySlotPos: EndcapBySlotPos = {}

  endcaps.forEach((e: Endcap) => {
    endcapsBySlotPos[e.slot_position] = e
  })
  return endcapsBySlotPos
}

const buildSearchProductParent = ({
  variants,
}: SearchProductAPI): NonNullable<ProductAPI['parent']> | undefined => {
  if (!variants.values?.length) return undefined

  const variationProducts = variants.values.map((variant) => ({
    product_id: variant.id,
    size: variant.size ?? '',
    variation_color: variant.color ?? '',
    variation_size: variant.size ?? '',
  }))

  return {
    product_id: variants.group_id,
    total_colors: variants.colors.total,
    variation_colors: variants.colors.variants.map(({ name, swatch_url }) => ({
      color: name,
      swatch_url: swatch_url ?? undefined,
    })),
    variation_products: variationProducts,
  }
}

export const normalizeSearchProduct = (searchProduct: SearchProductAPI) => {
  const { id, properties, metadata, ads_properties, variants } = searchProduct
  const matchingVariant = variants.values?.find((variant) => variant.id === id)
  const parent = buildSearchProductParent(searchProduct)

  return {
    ...properties,
    id: Number(id),
    available: !properties.oos,
    beacons: ads_properties?.beacons,
    bought: metadata?.bought,
    boost_bought: metadata?.boost_bought,
    external_bought: metadata?.purchase_origin === 'cross_retailer',
    featured: ads_properties?.featured,
    featured_source: ads_properties?.featured_source,
    featured_transaction_id: ads_properties?.featured_transaction_id,
    for_you: metadata?.for_you,
    parent,
    parent_product_id: properties.parent_product_id,
    swatch_url: matchingVariant?.swatch_url,
    variation_color: matchingVariant?.color,
    variation_size: matchingVariant?.size,
  }
}

export const normalizeSearchResults = (
  searchResults: SearchResponse,
  params: SearchParams = {},
): SearchResults => {
  const { category_id: categoryId, promotion_category_id: promotionCategoryId } = params
  const { hits, endcaps, facets, shelves, coupons, ...restSearchResults } = searchResults
  const isBrowsing = Boolean(categoryId || promotionCategoryId)

  if (promotionCategoryId && hits) {
    // We have an issue with our backend API associating multiple promotions to a
    // product with we are dealing with the pains on the frontend. Product Team wants
    // there to be ONE promotion associated to a product so we are putting in this hack
    // in order to ensure the correct promotion is associated. Minimal Viable Hack...
    searchResults.hits = hits.map((hit) => {
      // only utilize hit.cpg_promotions after spend-get integration released
      if (hit.cpg_promotions) {
        hit.cpg_promotions = hit.cpg_promotions.filter(
          (promotion) => promotion.category_id === promotionCategoryId,
        )
      }
      return hit
    })
  }
  const normalizedEndcaps = normalizeEndcaps(endcaps)
  const normalizedFacets = normalizeFacets(facets, isBrowsing)
  const normalizedProducts = searchResults.hits?.map((product) => normalizeProduct(product))
  const normalizedShelves = shelves?.map(normalizeShelf) || []

  return {
    ...restSearchResults,
    coupons: coupons?.map(normalizeCoupon) ?? [],
    endcapsBySlotPos: normalizedEndcaps,
    facets: normalizedFacets,
    products: normalizedProducts,
    shelves: normalizedShelves,
  }
}

export const normalizeSuggestions = ({
  suggestions = [],
  image_suggestions = [],
  type: suggestionsType = '',
}: SuggestionsResponse): Suggestion[] => {
  return [...suggestions, ...image_suggestions].map((suggestion) => {
    return {
      ...suggestion,
      // we need to add the general suggestion type to the suggestion if the type
      // of the suggestion is a standard type so that data science can differentiate
      // between the different types of standard type suggestions (e.g. correction, popular, etc.)
      ...(suggestion.type === SUGGESTION_TYPES.STANDARD && {
        type: `${suggestionsType}_${suggestion.type}`,
      }),
    }
  })
}

export const calculateTotalPages = (totalResults: number): number => {
  const totalPages = totalResults / PURCHASE_HISTORY_PAGE_SIZE
  if (totalResults % PURCHASE_HISTORY_PAGE_SIZE === 0) return totalPages
  if (totalPages > 1) return Math.ceil(totalPages)
  if (totalPages > 0) return 1
  return 0
}

export const findCategoryFacet = (facets: Facet[]): CategoryFacet | null =>
  facets.find((f): f is CategoryFacet => f.facet === CATEGORY) ?? null

export const findCurrentCategoryByParams = (facets: Facet[], params: SearchParams) => {
  const { category_id, promotion_category_id } = params
  const id = Number(category_id || promotion_category_id)

  const categoryFacet = findCategoryFacet(facets)
  if (!categoryFacet) return

  return categoryFacet.items.find((c) => c.id === id)
}

export const findActiveStoreFilters = (stores: ShoppingStoreDetails[], params: SearchParams) => {
  const { contexts } = params
  if (!Array.isArray(contexts)) return []

  return stores.filter((s) => contexts.includes(String(s.context_value)))
}

export const isCurrentCategoryActive = (
  params: SearchParams = {},
  currentCategory?: SearchCategory,
) => {
  const { query, promotion_category_id, category_ids } = params
  // if there is no current category selected
  if (!currentCategory?.id) return false
  // feature promotion page where the promotion is different than the current category
  if (promotion_category_id) {
    return Number(promotion_category_id) !== Number(currentCategory.id)
  }
  // if the user is on the search page with a query and a category selected
  if (query) return true
  // if the user selected a H2 or H3 category which has a parent category
  if (currentCategory.parentId) return true

  // from global category search page, if an h1 is selected
  if (category_ids) return true
}

export const getAllowedFacetsForParams = (params: SearchParams): string[] => {
  const { H2_CATEGORIES, H3_CATEGORIES } = SEARCH_FACETS
  const { query, category_id: categoryId, category_ids } = params

  if (categoryId) return Object.values(SEARCH_FACETS)
  if (query || category_ids?.length) return without(Object.values(SEARCH_FACETS), H3_CATEGORIES)
  return without(Object.values(SEARCH_FACETS), H2_CATEGORIES, H3_CATEGORIES)
}

/*
 * The parse util function we use to manipulate the query string from the URL
 * parses the string into an object where all keys are strings. This is an
 * intentional design decision.
 *
 * However, our Search service breaks if you give it strings where it expects
 * a boolean or int, so type coercion is necessary to transform some string
 * keys into the type the Search service expects. Any component that would like
 * to make use of the query params object does not need to worry about coercing
 * any values itself, it can just import this function and use the result. This
 * should only be used for query params that are used by Search related services
 * such as Search / Purchase history / Universal Search and etc.
 */

export function coerceObjectValues(urlQuery: ParsedUrlQuery = {}) {
  const searchParams: SearchParams = {}

  return Object.keys(urlQuery)
    .filter(isValidSearchParam)
    .reduce((params, key) => {
      const value = urlQuery[key]
      if (!value) return params
      switch (key) {
        case 'sort':
        case 'query':
        case 'buy_again_query':
        case 'zip':
        case 'category_name': {
          if (typeof value !== 'string') return params
          params[key] = String(value)
          return params
        }
        case 'flavors':
        case 'brands':
        case 'contexts': {
          params[key] = Array.isArray(value) ? value : [value]
          return params
        }

        case 'category_id':
        case 'promotion_category_id': {
          if (isNaN(Number(value))) return params
          params[key] = Number(value)
          return params
        }
        case 'new_deals':
        case 'active_deals':
        case 'coupon_only': {
          if (value === 'false') params[key] = false
          else if (value === 'true') params[key] = true
          return params
        }
        case 'category_ids': {
          if (typeof value !== 'string') return params
          try {
            params[key] = JSON.parse(value)
          } catch {
            logError('invalid category_ids passed in search url', { value })
            return params
          }
          return params
        }
        default:
          return params
      }
    }, searchParams)
}

const isValidSearchParam = (param: string): param is keyof SearchParams => {
  return param in validSearchFilterParams
}

export const getSelectedParams = (queryParam: string | string[]): string[] => {
  if (!queryParam) return []
  return Array.isArray(queryParam) ? queryParam : [queryParam]
}
